import { Button } from '@ui/shared/Button';
import * as React from 'react';
import styled from 'styled-components';
import { Icon } from '../../../shared/Icon';

/**
 * Props for the UnableToSendVerification component
 */
export interface IUnableToSendVerificationProps {
  onStartOver: () => void;
}

/**
 * UnableToSendVerification component - Displayed when MFA code delivery fails
 * Provides user with explanation and option to start over
 */
export const UnableToSendVerification: React.FC<
  IUnableToSendVerificationProps
> = ({ onStartOver }: IUnableToSendVerificationProps): React.ReactElement => {
  return (
    <Container>
      <h1 style={{ margin: '.5rem 1rem' }}>Verification Error</h1>
      <ContentContainer>
        <IconContainer>
          <Icon name='error_outline' size='large' color='#dc3545' />
        </IconContainer>
        <Message>Unable to Send Verification Code</Message>
        <SubMessage>
          We're unable to send a multi-factor verification code from this
          device. This may be due to network connectivity issues or device
          restrictions.
        </SubMessage>
        <SupportMessage>
          If this problem persists, please contact support at{' '}
          <SupportLink href='mailto:techsupport@hawkindynamics.com'>
            techsupport@hawkindynamics.com
          </SupportLink>
        </SupportMessage>
        <ButtonContainer>
          <Button
            icon='restart_alt'
            title='Start Over'
            type='button'
            onClick={onStartOver}
            flavor='primary'
           />
        </ButtonContainer>
      </ContentContainer>
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 30px 20px;
  width: 100%;
  min-height: 200px;
`;

const IconContainer = styled.div`
  margin-bottom: 20px;
`;

const Message = styled.p`
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text);
  margin: 0 0 16px 0;
  text-align: center;
`;

const SubMessage = styled.p`
  font-size: 14px;
  color: var(--color-text-secondary, #666);
  margin: 0 0 12px 0;
  text-align: center;
  line-height: 1.5;
  max-width: 400px;
`;

const SupportMessage = styled.p`
  font-size: 13px;
  color: var(--color-text-secondary, #666);
  margin: 0 0 24px 0;
  text-align: center;
  line-height: 1.5;
  max-width: 400px;
`;

const SupportLink = styled.a`
  color: #42a5f5;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 8px;
`;
